import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { cn } from '../lib/cn';
import { useOnClickOutside } from '../hooks/useOnClickOutside';

export interface FilterPopoverPillOption<T extends string> {
  value: T;
  label: string;
}

export interface FilterPopoverPillProps<T extends string> {
  label: string;
  options: FilterPopoverPillOption<T>[];
  selected: T[];
  onChange: (next: T[]) => void;
  /**
   * V2.1-G — countNoun lets each consumer phrase the active label
   * with an appropriate noun and Danish singular/plural form. When
   * supplied, the pill renders `${n} ${singular|plural} valgt` (no
   * "{label} er" prefix). Falls back to the V1.5.1 format
   * `${label} er ${n} valgt` when both `countNoun` and `formatCount`
   * are absent.
   */
  countNoun?: { singular: string; plural: string };
  /**
   * Full override for the active-pill text. Wins over `countNoun`.
   * Default behaviour is `formatCountDefault(n, label, countNoun)`.
   */
  formatCount?: (n: number, label: string) => string;
  align?: 'start' | 'end';
  /** Render "Vælg alle" / "Ryd" row below the checkbox list. Default true. */
  allowSelectAll?: boolean;
  disabled?: boolean;
}

/**
 * V2.1-G — default label formatter.
 *
 * - With a `countNoun`: `"3 statusser valgt"` / `"1 status valgt"`.
 * - Without: legacy `"Status er 3 valgt"` (kept so callers that
 *   haven't yet been migrated continue to work).
 */
export function formatCountDefault(
  n: number,
  label: string,
  countNoun?: { singular: string; plural: string },
): string {
  if (countNoun) {
    const noun = n === 1 ? countNoun.singular : countNoun.plural;
    return `${n} ${noun} valgt`;
  }
  return `${label} er ${n} valgt`;
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function FilterPopoverPill<T extends string>({
  label,
  options,
  selected,
  onChange,
  countNoun,
  formatCount,
  align = 'start',
  allowSelectAll = true,
  disabled = false,
}: FilterPopoverPillProps<T>) {
  const renderCount: (n: number, l: string) => string =
    formatCount ?? ((n, l) => formatCountDefault(n, l, countNoun));
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const pillButtonRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();

  const applied = selected.length > 0;

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useOnClickOutside(containerRef, close, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        pillButtonRef.current?.focus();
        return;
      }
      if (e.key === 'Tab') {
        const popover = popoverRef.current;
        if (!popover) return;
        const focusable = popover.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const popover = popoverRef.current;
    if (!popover) return;
    const first = popover.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [open]);

  const toggleOption = (value: T) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const onPillKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) setOpen((v) => !v);
    }
  };

  const popoverPos = align === 'end' ? 'right-0' : 'left-0';

  return (
    <div ref={containerRef} role="group" aria-label={label} className="relative inline-block">
      {applied ? (
        <div className="inline-flex items-stretch overflow-hidden rounded-token-pill border border-primary/30 bg-primary-tint">
          <button
            ref={pillButtonRef}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setOpen((v) => !v)}
            onKeyDown={onPillKey}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            className={cn(
              'flex h-8 items-center gap-1.5 px-3 text-ui font-medium text-primary transition-colors duration-2 ease-brand',
              'hover:bg-primary-tint/60 focus-visible:shadow-focus focus-visible:outline-none',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <span>{renderCount(selected.length, label)}</span>
            <Chevron open={open} />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={clearAll}
            aria-label={`Ryd ${label}-filter`}
            className={cn(
              'flex h-8 items-center border-l border-primary/20 px-2 text-primary/70 transition-colors duration-2 ease-brand',
              'hover:bg-primary-tint/50 hover:text-primary focus-visible:shadow-focus focus-visible:outline-none',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <CloseIcon />
          </button>
        </div>
      ) : (
        <button
          ref={pillButtonRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
          onKeyDown={onPillKey}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-token-pill border border-dashed border-line-2 bg-transparent px-3 text-ui text-ink-2 transition-colors duration-2 ease-brand',
            'hover:bg-paper-1 focus-visible:shadow-focus focus-visible:outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          <span>{label}</span>
          <Chevron open={open} />
        </button>
      )}

      {open ? (
        <div
          ref={popoverRef}
          id={listboxId}
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          className={cn(
            'absolute z-50 mt-1.5 min-w-[220px] rounded-token-md border border-line-1 bg-paper-0 p-2 shadow-2',
            popoverPos,
          )}
        >
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-meta text-ink-3">Ingen valg tilgængelige</div>
          ) : (
            <ul className="flex flex-col">
              {options.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <li key={opt.value} role="option" aria-selected={checked}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-token-sm px-2 py-1.5 text-ui text-ink-1',
                        'hover:bg-paper-1',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOption(opt.value)}
                        className="accent-primary"
                      />
                      <span>{opt.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {allowSelectAll && options.length > 0 ? (
            <div className="mt-1 flex items-center justify-between border-t border-line-1 px-1 pt-2">
              <button
                type="button"
                onClick={selectAll}
                className="rounded-token-sm px-2 py-1 text-meta text-ink-1 hover:bg-paper-1 focus-visible:shadow-focus focus-visible:outline-none"
              >
                Vælg alle
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="rounded-token-sm px-2 py-1 text-meta text-ink-1 hover:bg-paper-1 focus-visible:shadow-focus focus-visible:outline-none"
              >
                Ryd
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      className={cn('transition-transform duration-2 ease-brand', open && 'rotate-180')}
    >
      <path
        d="M2.5 4.5L6 8L9.5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
